import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute, AdminRoute, AdminOrTeamLeaderRoute, PublicRoute, CRMRoute } from "./components/ProtectedRoute";

// Auth Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SetupPage from "./pages/SetupPage";

// App Pages
import DailyLogPage from "./pages/DailyLogPage";
import HistoryPage from "./pages/HistoryPage";
import ProfilePage from "./pages/ProfilePage";
import TeamLogsPage from "./pages/TeamLogsPage";
import DashboardPage from "./pages/DashboardPage";
import LeaderboardPage from "./pages/LeaderboardPage";

// Admin Pages
import UsersPage from "./pages/admin/UsersPage";
import ReportsPage from "./pages/admin/ReportsPage";
import TeamsPage from "./pages/admin/TeamsPage";
import AllLogsPage from "./pages/admin/AllLogsPage";
import SettingsPage from "./pages/admin/SettingsPage";
import UserReportPage from "./pages/admin/UserReportPage";
import EmailLogsPage from "./pages/admin/EmailLogsPage";
import IndividualUserReportPage from "./pages/admin/IndividualUserReportPage";
import UserDashboardPage from "./pages/admin/UserDashboardPage";
import FormsPage from "./pages/admin/FormsPage";

// CRM Pages
import CompaniesPage from "./pages/admin/CompaniesPage";
import CompanyDetailPage from "./pages/admin/CompanyDetailPage";
import LeadsPage from "./pages/admin/LeadsPage";
import LeadDetailPage from "./pages/admin/LeadDetailPage";
import TemplatesPage from "./pages/admin/TemplatesPage";
import TasksPage from "./pages/admin/TasksPage";
import SocialPage from "./pages/admin/SocialPage";
import InboxPage from "./pages/admin/InboxPage";
import AICallCampaignsPage from "./pages/admin/AICallCampaignsPage";
import SequencesPage from "./pages/admin/SequencesPage";

// Franchise Pages
import FranchiseLeadsPage from "./pages/admin/FranchiseLeadsPage";
import FranchiseListsPage from "./pages/admin/FranchiseListsPage";
import FranchiseListDetailPage from "./pages/admin/FranchiseListDetailPage";
import FranchiseTasksPage from "./pages/admin/FranchiseTasksPage";
import FranchiseLeadDetailPage from "./pages/admin/FranchiseLeadDetailPage";

// Document Library
import DocumentsPage from "./pages/admin/DocumentsPage";

// Knowledge Base
import KnowledgeBasePage from "./pages/admin/KnowledgeBasePage";

// Layout
import DashboardLayout from "./components/DashboardLayout";

import "./App.css";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/setup" element={<SetupPage />} />

          {/* Protected User Routes */}
          <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="/app/log" element={<DailyLogPage />} />
            <Route path="/app/history" element={<HistoryPage />} />
            <Route path="/app/profile" element={<ProfilePage />} />
            <Route path="/app/team-logs" element={<TeamLogsPage />} />
            <Route path="/app/leaderboard" element={<LeaderboardPage />} />
          </Route>

          {/* Admin & Team Leader Dashboard Route */}
          <Route element={<AdminOrTeamLeaderRoute><DashboardLayout /></AdminOrTeamLeaderRoute>}>
            <Route path="/app/dashboard" element={<DashboardPage />} />
            <Route path="/admin/logs" element={<AllLogsPage />} />
            <Route path="/admin/reports" element={<ReportsPage />} />
            <Route path="/admin/user-report" element={<IndividualUserReportPage />} />
            <Route path="/admin/user-dashboard" element={<UserDashboardPage />} />
          </Route>

          {/* CRM Routes - Admin or JobTalk Team members */}
          <Route element={<CRMRoute><DashboardLayout /></CRMRoute>}>
            <Route path="/admin/companies" element={<CompaniesPage />} />
            <Route path="/admin/companies/:id" element={<CompanyDetailPage />} />
            <Route path="/admin/leads" element={<LeadsPage />} />
            <Route path="/admin/leads/:id" element={<LeadDetailPage />} />
            <Route path="/admin/lists" element={<FranchiseListsPage />} />
            <Route path="/admin/lists/:id" element={<FranchiseListDetailPage />} />
            <Route path="/admin/templates" element={<TemplatesPage />} />
            <Route path="/admin/tasks" element={<TasksPage />} />
            <Route path="/admin/social" element={<SocialPage />} />
            <Route path="/admin/inbox" element={<InboxPage />} />
            <Route path="/admin/ai-calls" element={<AICallCampaignsPage />} />
            <Route path="/admin/sequences" element={<SequencesPage />} />
            <Route path="/admin/knowledge-base" element={<KnowledgeBasePage />} />
          </Route>

          {/* Franchise CRM Routes - Admin or Franchise Team members */}
          <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="/admin/franchise/leads" element={<FranchiseLeadsPage />} />
            <Route path="/admin/franchise/leads/:id" element={<FranchiseLeadDetailPage />} />
            <Route path="/admin/franchise/tasks" element={<FranchiseTasksPage />} />
          </Route>

          {/* Document Library - All authenticated users */}
          <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="/documents" element={<DocumentsPage />} />
          </Route>

          {/* Admin Only Routes */}
          <Route element={<AdminRoute><DashboardLayout /></AdminRoute>}>
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/teams" element={<TeamsPage />} />
            <Route path="/admin/forms" element={<FormsPage />} />
            <Route path="/admin/settings" element={<SettingsPage />} />
            <Route path="/admin/email-logs" element={<EmailLogsPage />} />
          </Route>

          {/* User Report - Admin and Team Leader can access */}
          <Route path="/admin/user-report/:userId" element={<AdminOrTeamLeaderRoute><UserReportPage /></AdminOrTeamLeaderRoute>} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/app/log" replace />} />
          <Route path="*" element={<Navigate to="/app/log" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
